import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ResolvedOperator } from './tenant.models';
import { extractTenantSlug } from './tenant-slug';

@Injectable({ providedIn: 'root' })
export class TenantService {
  private readonly http = inject(HttpClient);

  private readonly operatorState = signal<ResolvedOperator | null>(null);
  private readonly loadingState = signal(true);
  private readonly notFoundState = signal(false);
  private readonly attemptedSlugState = signal<string | null>(null);
  private readonly attemptedHostnameState = signal('');

  readonly operator = this.operatorState.asReadonly();
  readonly loading = this.loadingState.asReadonly();
  readonly notFound = this.notFoundState.asReadonly();
  readonly attemptedSlug = this.attemptedSlugState.asReadonly();
  readonly attemptedHostname = this.attemptedHostnameState.asReadonly();

  async resolve(): Promise<void> {
    const hostname = window.location.hostname;
    const slug = extractTenantSlug(hostname, environment.tenantBaseHost);

    this.attemptedHostnameState.set(hostname);
    this.attemptedSlugState.set(slug);

    if (!slug) {
      this.loadingState.set(false);
      this.notFoundState.set(true);
      return;
    }

    try {
      const operator = await firstValueFrom(
        this.http.get<ResolvedOperator>(
          `${environment.apiUrl}/operators/by-domain/${encodeURIComponent(slug)}`,
        ),
      );

      this.operatorState.set(operator);
      this.notFoundState.set(false);
    } catch {
      this.operatorState.set(null);
      this.notFoundState.set(true);
    } finally {
      this.loadingState.set(false);
    }
  }
}
